import type { TripOverviewItem } from '~/services/engine-ts';
import { tripOverviewToMapData } from './tripOverviewToMapData';

export type ItemRouteProgress = Map<string, { start: number; end: number }>;

export type ScrollCard = { id: string; start: number; end: number };

export const buildItemRouteProgress = (items: TripOverviewItem[]): ItemRouteProgress => {
  const { itemIdToProgress } = tripOverviewToMapData(items);
  const routeProgress: ItemRouteProgress = new Map();
  let previous = 0;
  for (const item of items) {
    const progress = itemIdToProgress.get(item.itemId) ?? previous;
    routeProgress.set(String(item.itemId), { start: item.type === 'leg' ? previous : progress, end: progress });
    previous = progress;
  }
  return routeProgress;
};

const cardProgress = (routeProgress: ItemRouteProgress, card: ScrollCard) =>
  routeProgress.get(card.id.split('-')[0]) ?? { start: 0, end: 0 };

const segments = (cards: ScrollCard[], routeProgress: ItemRouteProgress) =>
  cards.map((card, index) => {
    const next = cards[index + 1];
    return {
      card,
      start: card.start,
      end: next ? next.start : card.end,
      from: cardProgress(routeProgress, card).start,
      to: next ? cardProgress(routeProgress, next).start : cardProgress(routeProgress, card).end
    };
  });

export const routeProgressAtFocus = (
  cards: ScrollCard[],
  focus: number,
  routeProgress: ItemRouteProgress
): { activeId: string | null; progress: number } => {
  let active: ReturnType<typeof segments>[number] | undefined;
  for (const segment of segments(cards, routeProgress)) {
    if (segment.start > focus) break;
    active = segment;
  }
  if (!active) return { activeId: null, progress: 0 };
  const t = active.end > active.start ? Math.min(1, (focus - active.start) / (active.end - active.start)) : 1;
  return { activeId: active.card.id, progress: active.from + (active.to - active.from) * t };
};

export const focusForRouteProgress = (
  cards: ScrollCard[],
  progress: number,
  routeProgress: ItemRouteProgress
): number | null => {
  const all = segments(cards, routeProgress);
  for (const segment of all) {
    if (progress > segment.to) continue;
    const t = segment.to > segment.from ? Math.max(0, (progress - segment.from) / (segment.to - segment.from)) : 0;
    return segment.start + (segment.end - segment.start) * t;
  }
  return all.at(-1)?.end ?? null;
};

export const measureScrollerCards = (scroller: HTMLElement): ScrollCard[] => {
  const contentLeft = scroller.getBoundingClientRect().left - scroller.scrollLeft;
  return Array.from(scroller.querySelectorAll<HTMLElement>('[data-card-id]'), (card) => {
    const rect = card.getBoundingClientRect();
    return { id: card.dataset.cardId ?? '', start: rect.left - contentLeft, end: rect.right - contentLeft };
  });
};

const maxScrollLeft = (scroller: HTMLElement) => scroller.scrollWidth - scroller.clientWidth;

export const scrollerFocus = (scroller: HTMLElement) => {
  const max = maxScrollLeft(scroller);
  return max > 0 ? (scroller.scrollLeft / max) * scroller.scrollWidth : 0;
};

export const scrollerLeftForFocus = (scroller: HTMLElement, focus: number) =>
  (focus / scroller.scrollWidth) * maxScrollLeft(scroller);
